import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import InboxView from "../components/InboxView";
import { formatPhoneDisplay } from "../utils/phone";

/** Client portal inbox of leads that came from Digix Leads (same layout as the staff and agent inboxes). */
const TABS = [
  { id: "New", label: "New", tone: "#2563eb" },
  { id: "Working", label: "Working", tone: "#d97706" },
  { id: "Won", label: "Won", tone: "#16a34a" },
  { id: "Closed", label: "Closed", tone: "#64748b" },
  { id: "", label: "All", tone: "#1e3a8a" },
];

const inrFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const inr = (n) => (n === null || n === undefined ? "—" : `₹${inrFmt.format(Number(n))}`);
const shortDate = (v) => (v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const isConverted = (name) => String(name || "").trim().toLowerCase() === "converted";

function StatusModal({ lead, statuses, onClose, onDone }) {
  const [statusId, setStatusId] = useState(String(lead.Lead_Status_Id || ""));
  const [deal, setDeal] = useState(lead.Deal_Amount != null ? String(lead.Deal_Amount) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const chosen = statuses.find((s) => String(s.Status_Id) === statusId);
  const needsDeal = isConverted(chosen?.Status);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = { Lead_Status_Id: Number(statusId) };
      if (needsDeal) body.Deal_Amount = deal === "" ? null : Number(deal);
      await axiosInstance.put(`/client/leads/${lead.Real_Estate_Lead_Id}`, body);
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || "Could not update the lead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wl-modal-backdrop" role="presentation" onClick={busy ? undefined : onClose}>
      <form className="wl-modal cx-modal" role="dialog" aria-modal="true" aria-label="Update lead" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="wl-modal-head">
          <h3>Update {lead.Name}</h3>
        </div>
        <div className="cx-modal-body">
          <label className="wl-field">
            <span>Status</span>
            <select value={statusId} onChange={(e) => setStatusId(e.target.value)}>
              {statuses.map((s) => (
                <option key={s.Status_Id} value={s.Status_Id}>
                  {s.Status}
                </option>
              ))}
            </select>
          </label>
          {needsDeal ? (
            <label className="wl-field">
              <span>Deal amount (₹)</span>
              <input type="number" min="1" step="1" value={deal} onChange={(e) => setDeal(e.target.value)} placeholder="e.g. 4500000" />
              <span className="cx-hint">Needed for leads from Digix Leads when they are converted.</span>
            </label>
          ) : null}
          {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}
        </div>
        <div className="wl-modal-actions">
          <button type="button" className="wl-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="wl-btn wl-btn--primary" disabled={busy || (needsDeal && !deal)}>
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

export default function TenantDigixLeads() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("New");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([axiosInstance.get("/client/leads/digix"), axiosInstance.get("/client/lead-meta").catch(() => ({ data: { data: {} } }))])
      .then(([leadsRes, metaRes]) => {
        if (!active) return;
        setRows(Array.isArray(leadsRes.data?.data) ? leadsRes.data.data : []);
        setCounts(leadsRes.data?.counts || {});
        const meta = metaRes.data?.data || {};
        setStatuses(Array.isArray(meta.leadStatuses) ? meta.leadStatuses : []);
        setError("");
      })
      .catch((err) => active && setError(err.response?.data?.message || "Could not load your Digix Leads leads."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [reloadKey]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter(
      (r) => (!tab || r.Stage === tab) && (!term || `${r.Name} ${r.Mobile_Number} ${r.Project}`.toLowerCase().includes(term))
    );
  }, [rows, tab, search]);

  const actions = (r) => (
    <div className="cx-actions">
      <button type="button" className="wl-btn wl-btn--sm" onClick={() => navigate(`/portal/leads/${r.Real_Estate_Lead_Id}`)}>
        Open
      </button>
      <button type="button" className="wl-btn wl-btn--primary wl-btn--sm" onClick={() => setEditing(r)}>
        Update status
      </button>
    </div>
  );

  return (
    <div className="cportal-page">
      <div className="cportal-page-head">
        <h2>Digix Leads</h2>
        <p className="cx-hint">Leads Digix Leads has sent you. Mark a lead Converted with its deal amount when the sale closes.</p>
      </div>
      <InboxView
        tabs={TABS}
        counts={counts}
        tab={tab}
        onTab={setTab}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search name, mobile or project"
        onRefresh={() => setReloadKey((k) => k + 1)}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        error={error}
        loading={loading}
        rows={visible}
        rowKey={(r) => r.Real_Estate_Lead_Id}
        empty="No leads from Digix Leads here yet."
        columns={[
          { key: "lead", label: "Lead", render: (r) => (<><div className="wl-name">{r.Name}</div><div className="wl-sub">{shortDate(r.Created_On)}</div></>) },
          { key: "mobile", label: "Mobile", render: (r) => formatPhoneDisplay(r.Country_Code, r.Mobile_Number) || r.Mobile_Number },
          { key: "project", label: "Project or service", render: (r) => r.Project || "—" },
          { key: "source", label: "Source", render: () => "Digix Leads" },
          { key: "status", label: "Status", render: (r) => r.Status },
          { key: "deal", label: "Deal", render: (r) => inr(r.Deal_Amount) },
          { key: "actions", label: "Actions", className: "wl-actions-col", render: actions },
        ]}
        mobileCard={(r) => ({
          title: r.Name,
          meta: [{ label: r.Status, tone: r.Stage === "Won" ? "success" : r.Stage === "New" ? "warning" : "neutral" }],
          fields: [
            { label: "Mobile", value: formatPhoneDisplay(r.Country_Code, r.Mobile_Number) || r.Mobile_Number },
            { label: "Project", value: r.Project },
            { label: "Deal", value: r.Deal_Amount != null ? inr(r.Deal_Amount) : "" },
          ],
          actions: actions(r),
        })}
      />
      {editing ? (
        <StatusModal
          lead={editing}
          statuses={statuses}
          onClose={() => setEditing(null)}
          onDone={() => {
            setNotice({ type: "success", text: `${editing.Name} updated.` });
            setEditing(null);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
    </div>
  );
}
